/**
 * Application state (Zustand).
 *
 * The store is the ONLY place the project mutates. Because the project holds
 * bare name-parts + one namespace, `setNamespace` is a single-field change that
 * re-derives every id at export time (Section 9.2/9.4). Renaming a custom item
 * cascades to the recipe references that point at it.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GuideCategory, GuideEntry, Item, ItemRef, Language, Project, Recipe } from "../model/types";
import { newGuideCategory, newGuideEntry, newItem, newProject, newRecipe } from "../model/defaults";

export type PanelId = "settings" | "guide" | "items" | "recipes" | "languages" | "import" | "export";

interface UIState {
  panel: PanelId;
  selectedItemUuid?: string;
  selectedRecipeUuid?: string;
  activeLocale: string;
}

interface StoreState {
  project: Project;
  ui: UIState;

  // Navigation
  setPanel: (panel: PanelId) => void;
  selectItem: (uuid?: string) => void;
  selectRecipe: (uuid?: string) => void;
  setActiveLocale: (code: string) => void;

  // Project-level
  replaceProject: (project: Project) => void;
  resetProject: (packName?: string) => void;
  updateMeta: (patch: Partial<Project["meta"]>) => void;
  updateManifest: (patch: Partial<Project["manifest"]>) => void;

  // Identifiers (Section 9.4)
  setNamespace: (namespace: string) => void;
  setGuideItemId: (id: string) => void;

  // Items
  addItem: () => Item;
  updateItem: (uuid: string, patch: Partial<Item>) => void;
  renameItem: (uuid: string, newId: string) => void;
  removeItem: (uuid: string) => void;

  // Recipes
  addRecipe: () => Recipe;
  updateRecipe: (uuid: string, patch: Partial<Recipe>) => void;
  removeRecipe: (uuid: string) => void;

  // Guide (homepage + categories + entries)
  updateGuideTitle: (locale: string, value: string) => void;
  updateGuideIntro: (locale: string, value: string) => void;
  setGiveOnFirstJoin: (value: boolean) => void;
  addCategory: () => GuideCategory;
  updateCategory: (uuid: string, patch: Partial<GuideCategory>) => void;
  moveCategory: (uuid: string, dir: -1 | 1) => void;
  removeCategory: (uuid: string) => void;
  addEntry: (catUuid: string) => GuideEntry | undefined;
  updateEntry: (catUuid: string, entryUuid: string, patch: Partial<GuideEntry>) => void;
  moveEntry: (catUuid: string, entryUuid: string, dir: -1 | 1) => void;
  removeEntry: (catUuid: string, entryUuid: string) => void;

  // Languages
  addLanguage: (lang: Language) => void;
  removeLanguage: (code: string) => void;
}

function updateProject(set: any, get: any, fn: (p: Project) => Project) {
  set({ project: fn(get().project) });
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      project: newProject(),
      ui: { panel: "settings", activeLocale: "en_US" },

      setPanel: (panel) => set((s) => ({ ui: { ...s.ui, panel } })),
      selectItem: (uuid) => set((s) => ({ ui: { ...s.ui, selectedItemUuid: uuid } })),
      selectRecipe: (uuid) => set((s) => ({ ui: { ...s.ui, selectedRecipeUuid: uuid } })),
      setActiveLocale: (code) => set((s) => ({ ui: { ...s.ui, activeLocale: code } })),

      replaceProject: (project) =>
        set((s) => {
          // Prefer en_US for editing when the pack ships it; else the first language.
          const locale = project.languages.some((l) => l.code === "en_US")
            ? "en_US"
            : project.languages[0]?.code ?? "en_US";
          return {
            project,
            ui: { ...s.ui, activeLocale: locale, selectedItemUuid: undefined, selectedRecipeUuid: undefined },
          };
        }),
      resetProject: (packName) =>
        set({ project: newProject(packName), ui: { panel: "settings", activeLocale: "en_US" } }),

      updateMeta: (patch) => updateProject(set, get, (p) => ({ ...p, meta: { ...p.meta, ...patch } })),
      updateManifest: (patch) => updateProject(set, get, (p) => ({ ...p, manifest: { ...p.manifest, ...patch } })),

      // Single source of truth: nothing composed is stored, so this is enough.
      setNamespace: (namespace) => updateProject(set, get, (p) => ({ ...p, namespace })),
      setGuideItemId: (id) => updateProject(set, get, (p) => ({ ...p, guideItemId: id })),

      addItem: () => {
        const item = newItem(uniqueId("new_item", get().project.items.map((i) => i.id)));
        updateProject(set, get, (p) => ({ ...p, items: [...p.items, item] }));
        set((s) => ({ ui: { ...s.ui, panel: "items", selectedItemUuid: item.uuid } }));
        return item;
      },
      updateItem: (uuid, patch) =>
        updateProject(set, get, (p) => ({ ...p, items: p.items.map((i) => (i.uuid === uuid ? { ...i, ...patch } : i)) })),
      renameItem: (uuid, newId) =>
        updateProject(set, get, (p) => {
          const target = p.items.find((i) => i.uuid === uuid);
          if (!target) return p;
          const oldId = target.id;
          if (oldId === newId) return p;
          // Tags share the id space with items but are a different thing, so a
          // tag that happens to match the old item id must NOT be rewritten.
          const remap = (ref: ItemRef): ItemRef =>
            ref.kind === "project" && !ref.tag && ref.id === oldId ? { kind: "project", id: newId } : ref;
          return {
            ...p,
            items: p.items.map((i) => (i.uuid === uuid ? { ...i, id: newId } : i)),
            // Cascade every project reference that pointed at the old id.
            recipes: p.recipes.map((r) => ({
              ...r,
              ingredients: r.ingredients.map(remap),
              key: Object.fromEntries(Object.entries(r.key).map(([k, ref]) => [k, remap(ref)])),
              result: remap(r.result),
            })),
          };
        }),
      removeItem: (uuid) =>
        updateProject(set, get, (p) => ({ ...p, items: p.items.filter((i) => i.uuid !== uuid) })),

      addRecipe: () => {
        const recipe = newRecipe(uniqueId("new_recipe", get().project.recipes.map((r) => r.id)));
        updateProject(set, get, (p) => ({ ...p, recipes: [...p.recipes, recipe] }));
        set((s) => ({ ui: { ...s.ui, panel: "recipes", selectedRecipeUuid: recipe.uuid } }));
        return recipe;
      },
      updateRecipe: (uuid, patch) =>
        updateProject(set, get, (p) => ({ ...p, recipes: p.recipes.map((r) => (r.uuid === uuid ? { ...r, ...patch } : r)) })),
      removeRecipe: (uuid) =>
        updateProject(set, get, (p) => ({ ...p, recipes: p.recipes.filter((r) => r.uuid !== uuid) })),

      updateGuideTitle: (locale, value) =>
        updateProject(set, get, (p) => ({ ...p, guide: { ...p.guide, title: { ...p.guide.title, [locale]: value } } })),
      updateGuideIntro: (locale, value) =>
        updateProject(set, get, (p) => ({ ...p, guide: { ...p.guide, intro: { ...p.guide.intro, [locale]: value } } })),
      setGiveOnFirstJoin: (value) =>
        updateProject(set, get, (p) => ({ ...p, guide: { ...p.guide, giveOnFirstJoin: value } })),

      addCategory: () => {
        const cat = newGuideCategory(uniqueId("new_category", get().project.guide.categories.map((c) => c.id)));
        updateProject(set, get, (p) => ({ ...p, guide: { ...p.guide, categories: [...p.guide.categories, cat] } }));
        return cat;
      },
      updateCategory: (uuid, patch) =>
        updateProject(set, get, (p) => ({
          ...p,
          guide: { ...p.guide, categories: p.guide.categories.map((c) => (c.uuid === uuid ? { ...c, ...patch } : c)) },
        })),
      moveCategory: (uuid, dir) =>
        updateProject(set, get, (p) => {
          const cats = [...p.guide.categories];
          const idx = cats.findIndex((c) => c.uuid === uuid);
          const target = idx + dir;
          if (idx === -1 || target < 0 || target >= cats.length) return p;
          [cats[idx], cats[target]] = [cats[target], cats[idx]];
          return { ...p, guide: { ...p.guide, categories: cats } };
        }),
      removeCategory: (uuid) =>
        updateProject(set, get, (p) => ({
          ...p,
          guide: { ...p.guide, categories: p.guide.categories.filter((c) => c.uuid !== uuid) },
        })),

      addEntry: (catUuid) => {
        const cat = get().project.guide.categories.find((c) => c.uuid === catUuid);
        if (!cat) return undefined;
        const entry = newGuideEntry(uniqueId("new_entry", cat.entries.map((e) => e.id)));
        updateProject(set, get, (p) => ({
          ...p,
          guide: {
            ...p.guide,
            categories: p.guide.categories.map((c) => (c.uuid === catUuid ? { ...c, entries: [...c.entries, entry] } : c)),
          },
        }));
        return entry;
      },
      updateEntry: (catUuid, entryUuid, patch) =>
        updateProject(set, get, (p) => ({
          ...p,
          guide: {
            ...p.guide,
            categories: p.guide.categories.map((c) =>
              c.uuid === catUuid
                ? { ...c, entries: c.entries.map((e) => (e.uuid === entryUuid ? { ...e, ...patch } : e)) }
                : c,
            ),
          },
        })),
      moveEntry: (catUuid, entryUuid, dir) =>
        updateProject(set, get, (p) => ({
          ...p,
          guide: {
            ...p.guide,
            categories: p.guide.categories.map((c) => {
              if (c.uuid !== catUuid) return c;
              const entries = [...c.entries];
              const idx = entries.findIndex((e) => e.uuid === entryUuid);
              const target = idx + dir;
              if (idx === -1 || target < 0 || target >= entries.length) return c;
              [entries[idx], entries[target]] = [entries[target], entries[idx]];
              return { ...c, entries };
            }),
          },
        })),
      removeEntry: (catUuid, entryUuid) =>
        updateProject(set, get, (p) => ({
          ...p,
          guide: {
            ...p.guide,
            categories: p.guide.categories.map((c) =>
              c.uuid === catUuid ? { ...c, entries: c.entries.filter((e) => e.uuid !== entryUuid) } : c,
            ),
          },
        })),

      addLanguage: (lang) =>
        updateProject(set, get, (p) =>
          p.languages.some((l) => l.code === lang.code) ? p : { ...p, languages: [...p.languages, lang] },
        ),
      removeLanguage: (code) =>
        updateProject(set, get, (p) =>
          p.languages.length <= 1 ? p : { ...p, languages: p.languages.filter((l) => l.code !== code) },
        ),
    }),
    {
      name: "guidebook-generator:project",
      partialize: (s) => ({ project: s.project }),
    },
  ),
);

function uniqueId(base: string, existing: string[]): string {
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

// --- Project JSON import/export (save/load the editable document) ----------

export function serializeProject(project: Project): string {
  return JSON.stringify(project, null, 2);
}

export function parseProjectJson(text: string): Project {
  const parsed = JSON.parse(text);
  if (typeof parsed !== "object" || parsed === null || typeof parsed.namespace !== "string") {
    throw new Error("This does not look like a Guidebook Generator project file.");
  }
  return parsed as Project;
}
